import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useAppSelector } from 'src/services/redux/app/store';

const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}`;

export interface Period {
  id: number;
  company_id: number;
  name: string;
  is_active: boolean;
  start_date: string | null; // YYYY-MM-DD
  end_date: string | null; // YYYY-MM-DD
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PeriodSuggest {
  name: string;
  start_date: string;
  end_date: string;
}

export const PERIODS_LIST_KEY = 'periods-list';

export const usePeriodsList = (params: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id, user_id: user?.id, ...params };
  const requestUrl = REACT_APP_API_URL + '/periods/list';
  const fetchData = async () => {
    const res = await axios.get<any>(`${requestUrl}`, { params: requestParams });
    return res?.data || {};
  };
  return useQuery([PERIODS_LIST_KEY], fetchData, {
    refetchOnWindowFocus: false,
    onError,
    staleTime: Infinity,
    retry: false,
  });
};

/** Bir sonraki sezon için önerilen ad ve tarih aralığı. `enabled` ile tetiklenir (modal açıldığında). */
export const usePeriodsSuggest = (enabled: boolean, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const requestParams = { company_id: user?.company_id, user_id: user?.id };
  const requestUrl = REACT_APP_API_URL + '/periods/suggest';
  const fetchData = async () => {
    const res = await axios.get<PeriodSuggest>(`${requestUrl}`, { params: requestParams });
    return res?.data;
  };
  return useQuery(['periods-suggest'], fetchData, {
    enabled: enabled && !!user?.company_id,
    refetchOnWindowFocus: false,
    cacheTime: 0,
    staleTime: 0,
    retry: false,
    onError,
  });
};

const usePeriodsMutation = (path: string, onSuccess?: any, onError?: any) => {
  const { user } = useAppSelector((state) => state.auth);
  const queryClient = useQueryClient();
  const requestUrl = REACT_APP_API_URL + path;
  return useMutation(
    (formPayload: any) => {
      return axios.post(`${requestUrl}`, { ...formPayload, company_id: user?.company_id, user_id: user?.id });
    },
    {
      onError,
      onSuccess: (...args) => {
        queryClient.invalidateQueries([PERIODS_LIST_KEY]);
        onSuccess?.(...args);
      },
    },
  );
};

export const usePeriodsDelete = (onSuccess?: any, onError?: any) => usePeriodsMutation('/periods/delete', onSuccess, onError);
export const usePeriodsUpdate = (onSuccess?: any, onError?: any) => usePeriodsMutation('/periods/update', onSuccess, onError);
export const usePeriodsCreate = (onSuccess?: any, onError?: any) => usePeriodsMutation('/periods/create', onSuccess, onError);
/** Aktif sezonu kapatır, yeni sezonu oluşturup aktifleştirir. Yanıt: `{ period, closed }` */
export const usePeriodsOpen = (onSuccess?: any, onError?: any) => usePeriodsMutation('/periods/open', onSuccess, onError);
/** `{ id }` → sezonu kapatır. */
export const usePeriodsClose = (onSuccess?: any, onError?: any) => usePeriodsMutation('/periods/close', onSuccess, onError);
/** `{ id }` → sezonu yeniden aktifleştirir, diğerleri pasife alınır. */
export const usePeriodsActivate = (onSuccess?: any, onError?: any) => usePeriodsMutation('/periods/activate', onSuccess, onError);
